import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import React, { useCallback, useEffect, useRef, useState } from "react"
import useTechnicalSupportApi from "modules/technicalSupport"
import { styles } from "./styles/MessageDetail.styles"
import MessageComment from "./MessageComment"
import { useTranslation } from "react-i18next"
import CloseIcon from "@mui/icons-material/Close"
import {
  FaPaperPlane,
  FaPaperclip,
  FaMicrophoneAlt,
  FaRegPlayCircle,
  FaTimes,
  FaRegStopCircle,
  FaSpellCheck,
} from "react-icons/fa"
import useTeacherMessagesApi from "modules/teacherMessages"
import KeyPicker from "components/Tools/TeacherMessage/KeyPicker"
import MyEmojiPicker from "components/Tools/TeacherMessage/EmojiPicker"
import { ImageZoom } from "components/UI"
import useRecording from "hooks/useRecording"
import useSound from "hooks/useSound"
import { startRecordingBip, stopRecordingBip } from "utils/sounds"
import { useAuthContext } from "contexts/AuthContext"
import withReactContent from "sweetalert2-react-content"
import Swal from "sweetalert2"
import useActivityApi from "modules/activities"
import { routes } from "common/navigationRoutes"
import { ACTIVITY_VIEW_MODES } from "pages/student/activity/common/constants"
import { useNavigate } from "react-router-dom"
import { isRunningFromIframe } from "common/functions"

export default function MessageDetailModal({
  reportId,
  teacherName = "",
  visible = false,
  onClose = () => {},
}) {
  const { t } = useTranslation()
  const { openCloseTechnicalReport, addReplyReport } = useTechnicalSupportApi()
  const { getTechnicalReport } = useTeacherMessagesApi()
  const { getContentByGrade } = useActivityApi()
  const { audioSettings, selectedGroupData } = useAuthContext()
  const [messages, setMessages] = useState([])
  const [reportDetails, setReportDetails] = useState(null)
  const [screenshotModalOpen, setScreenshotModalOpen] = useState(false)
  const [currentRecording, setCurrentRecording] = useState(null)
  const [attachedImage, setAttachedImage] = useState(null)
  const [
    recordingURL,
    isRecording,
    startRecording,
    stopRecording,
    initStream,
    resetRecorder,
  ] = useRecording()
  const [playStart] = useSound(startRecordingBip, {
    volume: 0.5,
    soundEnabled: audioSettings?.enabledAudioEffects,
  })
  const [playStop] = useSound(stopRecordingBip, {
    volume: 0.5,
    soundEnabled: audioSettings?.enabledAudioEffects,
  })
  const inputRef = useRef(null)
  const isBusyRef = useRef(false)
  const scrollRef = useRef(null)
  const MySwal = withReactContent(Swal)
  const navigate = useNavigate()

  useEffect(() => {
    if (visible) {
      setCurrentRecording(null)
      setAttachedImage(null)
      fillDetails().catch((error) => {
        console.error("Error fetching message details:", error)
      })
    }
    return () => {
      resetRecorder(initStream)
    }
  }, [visible])

  useEffect(() => {
    if (!recordingURL || recordingURL === null) return
    setCurrentRecording(URL.createObjectURL(recordingURL))
  }, [recordingURL])

  const fillDetails = async () => {
    const details = await getTechnicalReport({ reportId })
    setMessages(details?.replies || [])
    setReportDetails(details?.report[0] || null)
    setTimeout(() => {
      // go to last scroll position
      if (scrollRef.current) {
        scrollRef.current.scrollTo({
          top: scrollRef.current.scrollHeight,
          behavior: "smooth",
        })
      }
    }, 500)
  }

  const onEmojiSelected = useCallback((emoji) => {
    const input = inputRef.current
    if (input) {
      // get input cursor position
      const cursorPosition = input.selectionStart
      // get input value
      const value = input.value
      // get text before cursor
      const textBefore = value.substring(0, cursorPosition)
      // get text after cursor
      const textAfter = value.substring(cursorPosition, value.length)
      // update input value by adding the key pressed
      input.value = textBefore + emoji + textAfter
      inputRef?.current?.setSelectionRange(
        cursorPosition + 1,
        cursorPosition + 1
      )
      setTimeout(() => {
        // focus input
        input.focus()
      }, 250)
    }
  }, [])

  const openScreenshot = useCallback(() => {
    setScreenshotModalOpen(true)
  }, [])

  const sendMessage = useCallback(async () => {
    if (
      (inputRef?.current?.value === "" || inputRef?.current?.value === null) &&
      currentRecording === null &&
      attachedImage === null
    ) {
      return
    }
    const comment = inputRef?.current?.value?.trim()
    const reportId = reportDetails?.reportId

    let attachFile = null
    if (attachedImage !== undefined && attachedImage !== null) {
      const attachedImageFile = await fetch(attachedImage)
      const attachedImageBlob = await attachedImageFile.blob()
      attachFile = new File([attachedImageBlob], "image.png", {
        type: "image/png",
      })
    }
    let attachedAudio = null
    if (
      currentRecording !== null &&
      currentRecording !== undefined &&
      currentRecording !== ""
    ) {
      const audioBlob = await fetch(currentRecording).then((r) => r.blob())
      const audiofile = new File([audioBlob], `audio.mp3`, {
        type: "application/mp3",
      })
      attachedAudio = audiofile
    }

    if (!isBusyRef.current) {
      isBusyRef.current = true
      addReplyReport({
        reportId,
        comment,
        imageFile: attachFile,
        audioFile: attachedAudio,
      })
        .then((response) => {
          if (response?.success) {
            fillDetails()
          }
        })
        .finally(() => {
          isBusyRef.current = false
          inputRef.current.value = ""
          setCurrentRecording(null)
          setAttachedImage(null)
        })
    }
  }, [
    attachedImage,
    currentRecording,
    reportDetails,
    selectedGroupData?.groupId,
  ])

  const recordAudio = useCallback(async () => {
    if (currentRecording !== null) return
    if (isRecording) {
      stopRecording()
      playStop()
    } else {
      console.log("Starting recording...")
      await startRecording()
      playStart()
    }
  }, [
    currentRecording,
    isRecording,
    startRecording,
    stopRecording,
    playStart,
    playStop,
  ])

  const playAudio = useCallback(
    (evt) => {
      if (currentRecording === null) return
      const audio = new Audio(currentRecording)
      audio.play()
    },
    [currentRecording]
  )

  const deleteRecording = useCallback((evt) => {
    evt.stopPropagation()
    evt.preventDefault()
    setCurrentRecording(null)
  }, [])

  const openAttachment = useCallback(() => {
    if (attachedImage !== null) return
    const input = document.createElement("input")
    input.type = "file"
    input.accept = "image/*"
    input.onchange = onImageSelected
    input.click()
  }, [attachedImage])

  const onImageSelected = useCallback((event) => {
    const selectedImage = URL.createObjectURL(event.target.files[0])
    setAttachedImage(selectedImage)
  }, [])

  const deleteAttachment = useCallback(() => {
    setAttachedImage(null)
  }, [])

  const closeTicket = useCallback(async () => {
    MySwal.fire({
      title: t("live_chat_close_question"),
      text: t("live_chat_close_text"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#008000",
      cancelButtonColor: "#d33",
      confirmButtonText: t("live_chat_close_accept"),
      cancelButtonText: t("Cancel"),
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        openCloseTechnicalReport({
          reportId: reportDetails?.reportId,
          closeReport: true,
        }).then((response) => {
          console.log(
            "🚀 ~ file: TicketDetail.js ~ line 103 ~ closeCurrentTicket ~ response",
            response
          )
          setReportDetails((prevDetails) => ({
            ...prevDetails,
            isOpen: false,
          }))
        })
      }
    })
  }, [reportDetails])

  const openActivity = useCallback(async () => {
    const response = await getContentByGrade({
      gradeId: reportDetails?.grade?.gradeId,
    })
    if (response) {
      onClose()
      const runningFromIframe = isRunningFromIframe()
      const activityUrl = `${routes.ACTIVITY}?groupId=${selectedGroupData?.groupId}&missionId=${response?.missionId}&missionModuleId=${response?.missionModuleId}&view=${ACTIVITY_VIEW_MODES.STUDENT}`
      if (!runningFromIframe) {
        navigate(activityUrl, { replace: true })
        window.location.reload()
      } else {
        window?.parent?.goToActivities({
          groupId: selectedGroupData?.groupId,
          missionId: response?.missionId,
          missionModuleId: response?.missionModuleId,
          view: ACTIVITY_VIEW_MODES.STUDENT,
          activityUrl: `${activityUrl}&authOrigin=parent`,
        })
      }
    }
  }, [selectedGroupData?.groupId, reportDetails?.grade?.gradeId])

  return (
    <React.Fragment>
      <Dialog
        open={visible}
        onClose={onClose}
        fullWidth
        PaperProps={{
          sx: styles.dialog,
        }}
      >
        <IconButton sx={styles.closeButton} onClick={onClose}>
          <CloseIcon onClick={onClose} sx={styles.closeIcon} />
        </IconButton>
        <DialogTitle
          textAlign={"center"}
          component={"div"}
          sx={styles.messageHeader}
        >
          <Typography variant="span" sx={styles.messageDetailUsername}>
            {t("message_to", {
              name: reportDetails?.teacherName || teacherName || "",
            })}
          </Typography>
          <Stack direction={"row"} justifyContent={"space-between"}>
            <Typography variant="span" sx={styles.messagePlatform} />
            <Typography variant="span" sx={styles.messageMetadata}>
              {reportDetails?.screenPath || ""}
            </Typography>
            <Typography
              variant="span"
              sx={styles.messageScreenshot}
              onClick={openScreenshot}
            />
          </Stack>
        </DialogTitle>
        <DialogContent sx={styles.messageDetailContent}>
          <Stack sx={styles.commentsContainer} ref={scrollRef}>
            {reportDetails?.grade && (
              <Stack position={"relative"} sx={styles.gradeDetails}>
                <Typography
                  sx={styles.studentExerciseLink}
                  onClick={openActivity}
                >
                  {t("StudentsExercise")}
                </Typography>
                <Stack sx={styles.grades}>
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    spacing={0.75}
                    sx={styles.gradeItem}
                  >
                    <Typography color="primary">
                      {t("PreviousGrade")}
                    </Typography>
                    <TextField
                      type="text"
                      min={0}
                      max={100}
                      step={1}
                      value={(
                        (reportDetails?.grade?.originalGrade * 100) /
                        reportDetails?.grade?.activityValue
                      ).toFixed(0)}
                      InputProps={{ sx: styles.gradeInput, readOnly: true }}
                    />
                    <Typography color="primary">{`/100`}</Typography>
                  </Stack>
                  <Stack direction={"row"} alignItems={"center"} spacing={0.75}>
                    <Typography color="primary">{t("new_grade")}</Typography>
                    <TextField
                      type="text"
                      min={0}
                      max={100}
                      step={1}
                      value={
                        !reportDetails?.grade?.updated
                          ? ""
                          : (
                              (reportDetails?.grade?.currentGrade * 100) /
                              reportDetails?.grade?.activityValue
                            ).toFixed(0)
                      }
                      InputProps={{ sx: styles.gradeInput, readOnly: true }}
                    />
                    <Typography color="primary">{`/100`}</Typography>
                  </Stack>
                </Stack>
                <Typography sx={styles.gradeIcon} variant="span">
                  <FaSpellCheck />
                </Typography>
              </Stack>
            )}
            {messages.map((message) => (
              <MessageComment key={message.replyId} {...message} />
            ))}
          </Stack>
          {reportDetails !== null && (
            <Typography
              variant="span"
              sx={[
                styles.messageDetailStatus,
                reportDetails?.isOpen && styles.messageStatusOpen,
              ]}
            >
              {reportDetails?.isOpen ? t("Ticket Open") : t("Ticket Closed")}
            </Typography>
          )}
        </DialogContent>
        {reportDetails?.isOpen && (
          <DialogActions component="div" sx={styles.messageFooter}>
            <Stack
              direction={"row"}
              width={"100%"}
              alignItems={"center"}
              pl={1}
              spacing={0.5}
              sx={styles.messageActions}
            >
              <KeyPicker onKeySelected={onEmojiSelected} />
              <MyEmojiPicker onEmojiSelected={onEmojiSelected} />
              <Button
                sx={[
                  styles.toolbarButton,
                  attachedImage !== null && styles.actionButtonRecorded,
                ]}
                startIcon={<FaPaperclip />}
                endIcon={
                  attachedImage !== null ? (
                    <Box onClick={deleteAttachment}>
                      <FaTimes />
                    </Box>
                  ) : null
                }
                onClick={openAttachment}
              >
                {attachedImage !== null ? t("uploaded_file") : t("attach_file")}
              </Button>
              <Button
                sx={[
                  styles.toolbarButton,
                  isRecording && styles.actionButtonRecording,
                  currentRecording !== null && styles.actionButtonRecorded,
                ]}
                startIcon={
                  currentRecording !== null ? (
                    <FaRegPlayCircle />
                  ) : (
                    <FaMicrophoneAlt />
                  )
                }
                endIcon={
                  isRecording ? (
                    <FaRegStopCircle />
                  ) : currentRecording !== null ? (
                    <Box onClick={deleteRecording}>
                      <FaTimes />
                    </Box>
                  ) : null
                }
                onClick={currentRecording !== null ? playAudio : recordAudio}
              >
                {isRecording
                  ? t("stop")
                  : currentRecording !== null
                  ? t("saved")
                  : t("record_audio")}
              </Button>
              <Button
                variant="contained"
                disableElevation
                sx={styles.closeTicket}
                onClick={closeTicket}
              >
                {t("Close Ticket")}
              </Button>
            </Stack>
            <Stack direction={"row"} width={"100%"} alignItems={"center"}>
              <TextField
                sx={styles.messageDetails}
                fullWidth
                multiline
                inputRef={inputRef}
                inputProps={{
                  maxLength: 1500,
                }}
              />
              <IconButton sx={styles.sendButton} onClick={sendMessage}>
                <FaPaperPlane />
              </IconButton>
            </Stack>
          </DialogActions>
        )}
      </Dialog>
      <Dialog
        open={screenshotModalOpen}
        fullWidth
        PaperProps={{
          sx: [styles.dialog, styles.filePreviewDialog],
        }}
      >
        <IconButton
          sx={styles.closeButton}
          onClick={() => setScreenshotModalOpen(false)}
        >
          <CloseIcon sx={styles.closeIcon} />
        </IconButton>
        <DialogContent>
          <ImageZoom src={reportDetails?.screenshotLink || ""} />
        </DialogContent>
      </Dialog>
    </React.Fragment>
  )
}
